import * as React from "react";
import { toast } from "react-toastify";
import {
  useRerunSnapshotCollectionColumnMutation,
  type SnapshotGridField,
} from "@/modules/snapshots/businessReports.api";
import type { BusinessReport } from "@/modules/snapshots/businessReports.types";

const REFUSAL_MESSAGES: Record<string, string> = {
  column_rerun_in_progress: "This column is already being generated.",
  referenced_column_pending: "A column this prompt references is still being generated.",
  review_run_in_progress: "A review is running for this column.",
  contract_summary_run_in_progress: "A contract summary run is using this collection.",
  pipeline_managed_collection: "This collection is managed by the Contract Families workflow.",
  family_collection_read_only: "Columns of this collection cannot be re-run.",
};

interface Input {
  collectionId: string | undefined;
  fields: SnapshotGridField[];
  businessReportPrompts: BusinessReport["prompts"] | undefined;
  onStarted: (columnName: string) => void;
}

/** "Re-run column" flow: which columns can re-run, the confirm dialog state and starting the re-run. */
export const useColumnRerun = ({ collectionId, fields, businessReportPrompts, onStarted }: Input) => {
  const [rerunDialogColumn, setRerunDialogColumn] = React.useState<string | null>(null);
  const [rerunColumn, { isLoading: isStartingRerun }] = useRerunSnapshotCollectionColumnMutation();

  const promptIdByColumn = React.useMemo(() => {
    const promptsById = new Map((businessReportPrompts ?? []).map(prompt => [prompt.id, prompt]));
    const out = new Map<string, string>();
    for (const field of fields) {
      if (field.is_note || !field.business_report_prompt_id) continue;
      const prompt = promptsById.get(field.business_report_prompt_id);
      if (prompt && prompt.data_type !== "relevancy") out.set(field.name, prompt.id);
    }
    return out;
  }, [fields, businessReportPrompts]);

  const rerunnableColumns = React.useMemo<ReadonlySet<string>>(
    () => new Set(promptIdByColumn.keys()),
    [promptIdByColumn],
  );

  const handleConfirmRerun = async () => {
    const promptId = rerunDialogColumn ? promptIdByColumn.get(rerunDialogColumn) : undefined;
    if (!collectionId || !promptId) return;
    try {
      const { name, rerun_cells: rerunCells } = await rerunColumn({
        snapshotCollectionId: collectionId,
        businessReportPromptId: promptId,
      }).unwrap();
      onStarted(name);
      toast.success(`Re-running "${name}" for ${rerunCells} ${rerunCells === 1 ? "snapshot" : "snapshots"}`);
      setRerunDialogColumn(null);
    } catch (error) {
      const detail = (error as { data?: { detail?: string } })?.data?.detail;
      toast.error((detail && REFUSAL_MESSAGES[detail]) ?? detail ?? "Failed to re-run column");
    }
  };

  return {
    rerunnableColumns,
    rerunDialogColumn,
    isStartingRerun,
    handleRerunColumnClick: setRerunDialogColumn,
    closeRerunDialog: () => setRerunDialogColumn(null),
    handleConfirmRerun,
  };
};
